// Analytics Event Types and Interfaces
//
// This module defines the core types and interfaces for the analytics system:
// - Standardized event schema for consistency across providers
// - Provider interface for implementing analytics destinations
// - Context types for global analytics properties

export interface AnalyticsEvent {
    /** Privacy at capture time: promotion must not replay pre-consent payloads. */
    consentMode?: import('../services/consentMode').ConsentMode;
    consentGeneration?: number;
    /** Event type identifier (e.g., 'widget_impression', 'message_sent') */
    type: EventType | string;

    /** Event-specific properties and data */
    properties: Record<string, unknown>;

    /** Global analytics context (domain, session, etc.) */
    context: AnalyticsContext;

    /** When the event occurred */
    timestamp: number;

    /** Optional event category for filtering */
    category?: EventCategory;
}

export interface AnalyticsContext {
    domain?: string; // Full domain with subdomains (e.g., "www.pennylane.com")
    rootDomain?: string; // Root domain without subdomains (e.g., "pennylane.com")
    sessionId?: string;
    lastActiveSessionId?: string; // Last session where user sent at least one message
    lastActiveSessionDate?: number; // Unix timestamp (milliseconds) when last session was active
    clientId?: string; // Stable client ID for traffic control bucketing (PostHog distinct_id or UUID)
    conversation_triggered_by?: MessageSourceType | null; // How the conversation was initially triggered (null = explicitly cleared)
    personId?: string; // Unique person identifier (PostHog distinct_id or fallback UUID)
    apiVersion?: string;
    frontendVersion?: string;
    deploymentTarget?: string; // "chrome-extension", "widget", "preprod", "backend-ui", "unknown", etc.
    environment?: string; // "development", "production", "staging", etc.
    gitCommitSha?: string; // Git commit SHA for release tracking
    rw_staff_experiment_id?: string;
    rw_staff_experiment_start_date?: string;
    rw_staff_experiment_assignment?: 'control' | 'treatment';
    rw_client_experiment_id?: string;
    rw_client_experiment_start_date?: string;
    rw_client_experiment_assignment?: 'control' | 'treatment';
    rw_rose_exposure_variant?: 'control' | 'treatment';
    rw_rose_exposure_reason?: 'staff_holdout' | 'client_experiment' | 'none';
}

export interface AnalyticsProvider {
    /** Unique provider identifier */
    readonly name: string;

    /** Determines which events this provider should receive */
    eventFilter(event: AnalyticsEvent): boolean;

    /** Send event to this provider's destination */
    track(event: AnalyticsEvent): Promise<void>;

    /** Whether this provider is currently enabled */
    isEnabled(): boolean;

    /** Optional: Initialize provider with context (called once on registration) */
    initialize?(context: AnalyticsContext): Promise<void>;

    /** Optional: Update provider context (called when context changes, no re-initialization) */
    updateContext?(context: Partial<AnalyticsContext>): void;

    /** Optional: Cleanup provider resources */
    destroy?(): Promise<void>;
}

export interface AnalyticsConfig {
    /** Global context applied to all events */
    globalContext?: AnalyticsContext;

    /** Whether to enable debug logging */
    debug?: boolean;

    /** Maximum time to wait for provider responses */
    timeoutMs?: number;

    /** Whether to continue if a provider fails */
    continueOnError?: boolean;

    /**
     * PostHog SDK init overrides. Deliberately NOT part of globalContext — context is
     * turned into posthog.register() super-properties and stamped on every event.
     */
    posthog?: {
        capturePageview?: boolean;
        capturePageleave?: boolean;
    };
}

// Event type constants for type safety
export const EventTypes = {
    // Widget events
    WIDGET_IMPRESSION: 'widget_impression',
    WIDGET_INTERACTION: 'widget_interaction',
    WIDGET_REVEALED_BY_SCROLL: 'widget_revealed_by_scroll',
    CLICK_HIDDEN_ZONE: 'click_hidden_zone',
    MOUSE_HOVER: 'mouse_hover',

    // User input events
    TYPED_INTO_INPUT: 'typed_into_input',
    MESSAGE_SENT: 'message_sent',

    // Dynamic questions
    DYNAMIC_QUESTION_DISPLAYED: 'dynamic_question_displayed',
    DYNAMIC_QUESTION_CLICKED: 'dynamic_question_clicked',
    MINIMIZED_DYNAMIC_QUESTIONS: 'minimized_dynamic_questions',

    // AI Sections
    AI_SECTION_CLICKED: 'ai_section_clicked',
    AI_SECTION_DISPLAYED: 'ai_section_displayed',

    // Follow-up questions
    FOLLOW_UP_QUESTIONS_DISPLAYED: 'follow_up_questions_displayed',
    FOLLOW_UP_QUESTION_CLICKED: 'follow_up_question_clicked',

    // Suggested answers
    SUGGESTED_ANSWERS_DISPLAYED: 'suggested_answers_displayed',
    SUGGESTED_ANSWER_CLICKED: 'suggested_answer_clicked',

    // Content recommendations
    CONTENT_RECOMMENDATION_DISPLAYED: 'content_recommendation_displayed',
    CONTENT_RECOMMENDATION_CLICKED: 'content_recommendation_clicked',

    // CTA events
    CTA_PAGE_DISPLAY: 'cta_page_display',
    CTA_RENDERED: 'cta_rendered',
    CTA_CLICKED: 'cta_clicked',
    // Click on the client's OWN website CTA (signup/demo link), matched by
    // destination URL. Distinct from CTA_CLICKED, which is Rose's own CTA.
    CLIENT_CTA_CLICKED: 'client_cta_clicked',

    // Form events
    FORM_SUBMITTED: 'form_submitted',
    FORM_SUBMISSION_FILTERED_OUT: 'form_submission_filtered_out',

    // Login events
    LOGIN_CLICKED: 'login_clicked',

    // System events
    POSTHOG_INITIALIZED: 'posthog_initialized',

    // Conversation events (for N8N)
    CONVERSATION_TRACKED: 'conversation_tracked',

    // Post-qualification events
    POST_QUALIFICATION_STARTED: 'post_qualification_started',
    POST_QUALIFICATION_QUESTION_DISPLAYED: 'post_qualification_question_displayed',
    POST_QUALIFICATION_ANSWER_SUBMITTED: 'post_qualification_answer_submitted',
    POST_QUALIFICATION_COMPLETED: 'post_qualification_completed',
    POST_QUALIFICATION_DISMISSED: 'post_qualification_dismissed',

    // Evaluation events
    EVALUATION_SUBMITTED: 'evaluation_submitted',
    EVALUATION_POPUP_SHOWN: 'evaluation_popup_shown',
    EVALUATION_POPUP_CANCELLED: 'evaluation_popup_cancelled',
    EVALUATION_THUMBS_CLICKED: 'evaluation_thumbs_clicked',
} as const;

type EventType = (typeof EventTypes)[keyof typeof EventTypes];

/**
 * Message source types - how a message or conversation was triggered
 */
export type MessageSourceType =
    | 'user_typed'
    | 'dynamic_question'
    | 'follow_up_question'
    | 'suggested_answer'
    | 'booking_cta'
    | 'ai_section'
    | 'ai_section_button'
    | 'ai_section_search_bar'
    | 'post_conversion_trigger'
    | 'booking_completed'
    // WebMCP agent tool call, `webmcp:<tool name>` (bare `webmcp` before IX-5319).
    | 'webmcp'
    | `webmcp:${string}`
    // Agent HTTP endpoint question, `agent_http:ask` (IX-5425).
    | 'agent_http'
    | `agent_http:${string}`;

// Event categories for provider filtering
export const EventCategories = {
    USER_ACTION: 'user_action',
    SYSTEM: 'system',
    CONVERSION: 'conversion',
    CONVERSATION: 'conversation',
    NAVIGATION: 'navigation',
    EVALUATION: 'evaluation',
} as const;

export type EventCategory = (typeof EventCategories)[keyof typeof EventCategories];
